/**
 * Sign-in (KAN-46).
 *
 * There is no password field, because the platform holds no passwords. Google
 * establishes who someone is, restricted to the firm's Workspace domain, and the
 * API decides whether that person has been granted access. Both halves are
 * enforced server-side: the domain against the signed `hd` claim, and access
 * against a row an administrator created.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import type { Permission, Role } from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { domainLabel, useAuthConfig } from '../lib/authConfig'
import { loadGoogleIdentity, type CredentialResponse } from '../lib/google'
import { useSession } from '../lib/session'
import { Alert } from '../ui/Alert'
import { Wordmark } from '../ui/Wordmark'
import { LockIcon } from '../ui/icons'

interface SignInResponse {
  token: string
  expiresAt: string | null
  user: {
    id: string
    fullName: string
    email: string
    role: Role
    permissions: Permission[]
  }
}

export function Login() {
  const { user, signIn } = useSession()
  const navigate = useNavigate()
  const location = useLocation()

  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)

  /** Where the visitor was headed before being bounced here, if anywhere. */
  const destination = (location.state as { from?: string } | null)?.from ?? '/'

  const config = useAuthConfig()

  const exchange = useCallback(
    async (response: CredentialResponse) => {
      if (!response.credential) {
        setError('Google did not return a sign-in. Try again.')
        return
      }

      setError(null)
      setSubmitting(true)

      try {
        const result = await apiFetch<SignInResponse>('/auth/google', {
          method: 'POST',
          body: JSON.stringify({ credential: response.credential }),
        })

        signIn(
          {
            id: result.user.id,
            fullName: result.user.fullName,
            email: result.user.email,
            role: result.user.role,
          },
          result.token
        )
        navigate(destination, { replace: true })
      } catch (caught) {
        setError(messageFor(caught))
      } finally {
        setSubmitting(false)
      }
    },
    [destination, navigate, signIn]
  )

  /**
   * The GIS callback is registered once, so it must not close over a stale
   * `exchange`. A ref keeps the registered function pointing at the current one.
   */
  const exchangeRef = useRef(exchange)
  exchangeRef.current = exchange

  useEffect(() => {
    if (!config.data || !buttonRef.current) return

    const container = buttonRef.current
    let cancelled = false

    loadGoogleIdentity()
      .then((google) => {
        if (cancelled) return

        google.initialize({
          client_id: config.data.googleClientId,
          callback: (response) => void exchangeRef.current(response),
          /**
           * A hint for the account chooser — passed only when there is a
           * single domain to hint at. `hd` takes one value, so with two
           * permitted domains it would hide half the accounts that can
           * actually sign in, which is worse than offering no hint. The API
           * enforces the full list either way.
           */
          ...(config.data.allowedDomains.length === 1
            ? { hd: config.data.allowedDomains[0] }
            : {}),
          /**
           * No silent re-authentication. On a shared machine, auto-selecting
           * the last account is how someone ends up looking at firm financials
           * signed in as a colleague.
           */
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        /** Cleared first so React's double-render in development cannot stack two buttons. */
        container.replaceChildren()
        google.renderButton(container, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
          /**
           * Pinned. Google localises the button from the browser's locale, so
           * without this it renders in a different language from the rest of
           * the interface — which reads as a broken page rather than a helpful
           * translation.
           */
          locale: 'en',
        })

        setGoogleReady(true)
      })
      .catch(() => {
        if (!cancelled) {
          setError('Google sign-in could not be loaded. Check your connection and reload.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [config.data])

  /**
   * Someone who is already signed in has no business on this screen — reaching
   * it by typing the URL, or by a bookmark saved before the session was
   * restored, should not offer to sign them in a second time. Placed after
   * every hook so the hook order does not depend on it.
   */
  if (user) {
    return <Navigate to={destination} replace />
  }

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/**
       * The indigo panel carries the brand and states plainly what the platform
       * holds, because the same sign-in screen is reached from outside the
       * office and someone landing on it should be able to tell whether they
       * are in the right place before signing in.
       */}
      <aside className="relative overflow-hidden bg-brand-900 px-6 py-10 sm:px-12 lg:flex lg:w-[46%] lg:flex-col lg:justify-between lg:py-14">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-800/70 blur-2xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-accent-700/25 blur-3xl"
        />

        <Wordmark className="relative" />

        <div className="relative mt-10 max-w-md lg:mt-0">
          <h2 className="font-display text-2xl leading-snug font-semibold text-white sm:text-3xl">
            Finance &amp; practice analytics for the firm.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-brand-200">
            Consolidated revenue, receivables, trust balances and cash flow, drawn from Clio and
            QuickBooks and traceable back to the source record.
          </p>

          <p className="mt-8 flex items-start gap-2.5 text-xs leading-relaxed text-brand-300">
            <LockIcon className="mt-px shrink-0" />
            <span>
              Access is granted per role, and every change to financial data is recorded against the
              account that made it.
            </span>
          </p>
        </div>

        <p className="relative mt-10 hidden text-xs text-brand-400 lg:block">
          Authorised use only. Divorce with a Plan.
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-2xl font-semibold text-brand-900">Sign in</h1>
          <p className="mt-1 mb-8 text-sm text-ink-600">
            {config.data ? (
              <>
                Use your{' '}
                <strong className="font-medium">{domainLabel(config.data.allowedDomains)}</strong>{' '}
                Google account.
              </>
            ) : (
              'Use your firm Google account.'
            )}
          </p>

          {config.error && (
            <Alert tone="error" className="mb-6">
              The sign-in service is unavailable. Try again shortly.
            </Alert>
          )}

          {/**
           * Google renders its own button into this node. It is not styled by
           * this app: Google's branding requirements govern it, and a
           * hand-drawn imitation wired to the same call would breach them.
           */}
          <div className="min-h-11" aria-busy={submitting}>
            <div ref={buttonRef} />

            {!googleReady && !config.error && !error && (
              <p className="text-sm text-ink-500">Loading Google sign-in…</p>
            )}
          </div>

          {submitting && <p className="mt-4 text-sm text-ink-600">Signing in…</p>}

          {error && (
            <Alert tone="error" className="mt-4">
              {error}
            </Alert>
          )}

          <p className="mt-8 text-xs leading-relaxed text-ink-600">
            Accounts are created by Finance before first sign-in — a firm Google account on its own
            does not grant access. Sessions last twelve hours and end on sign-out.
          </p>

          {/**
           * Small, and below the button rather than beside it: signing in is
           * the acceptance, so the terms have to be reachable before it —
           * without competing with the one action on the screen. Both are
           * public routes, so a reader who follows one can come back and sign
           * in without having lost anything.
           */}
          <p className="mt-6 border-t border-ink-100 pt-4 text-xs text-ink-500">
            By signing in you accept the{' '}
            <Link
              to="/legal/eula"
              className="underline decoration-ink-300 underline-offset-2 hover:text-brand-700"
            >
              End-User License Agreement
            </Link>{' '}
            and the{' '}
            <Link
              to="/legal/privacy"
              className="underline decoration-ink-300 underline-offset-2 hover:text-brand-700"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  )
}

/**
 * The API sends prose meant for the person reading it, plus a code. For the
 * cases where the message names the account or explains what to do, it is shown
 * as written; the rest collapse to one line, because "the token signature was
 * wrong" tells the reader nothing they can act on.
 */
function messageFor(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return 'Sign-in failed. Check your connection and try again.'
  }

  switch (caught.code) {
    case 'not_provisioned':
    case 'deactivated':
    case 'wrong_domain':
      return caught.message
    case 'replayed':
      return 'That sign-in had already been used. Try again.'
    case 'invalid_token':
      return 'That Google sign-in could not be verified. Try again.'
    default:
      return caught.status >= 500
        ? 'The server is unavailable. Try again shortly.'
        : 'Sign-in was not accepted.'
  }
}
